import { memo, useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Grid, Skeleton, Typography } from '@mui/material';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import {
  fetchTitleChildrenV2,
  fetchTitleMetadata,
  fetchTitleV2,
  getTitleVersions,
  setActiveTitleId,
  setActiveTitleLevel,
  setSelectedTitleId,
  updateActiveTitleHierarchy
} from '@/redux/slices/titleV2Slice';
import { selectPackagingActiveTemplate } from '@/redux/selectors/packagingSelectors';
import {
  selectActiveTitleLevel,
  selectActiveVersionsByTitleId,
  selectChildrenByTitleId,
  selectChildrenLoadingStatusByTitleId,
  selectIsActiveVersionsLoadingByTitle,
  selectRootTitle,
  selectSelectedTitleId,
  selectTitleMetadataById
} from '@/redux/selectors/titleV2Selectors';
import { selectSearchValue, selectSelectedHierarchy } from '@/redux/selectors/orderPlacementSelectors';
import { selectCanCreateEmbargoedTitleOrderPending, selectCanCreateEmbargoedTitleOrderProgress } from '@/redux/selectors/authSelectors';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import useCurrentBreakpoint from '@/components/_hooks/useCurrentBreakpoint';
import { LoadingStatuses } from '@/constants/constants';
import { useToast } from '@/components/_common/Toast';
import OnOffToggle from '@/components/_common/OnOffToggle';
import { ExpandedVersionRow, TitleVersionChip } from '@/exports/columnCommonComponents';
import TitleBrowserEntity from '@/components/OrderPlacement/OPStep2FileSelection/TitleBrowser/TitleBrowserEntity';
import TitleBrowserAccordion from '@/components/OrderPlacement/OPStep2FileSelection/TitleBrowser/TitleBrowserAccordion';
import TitleBrowserItemMenuV2 from '@/components/OrderPlacement/OPStep2FileSelection/v2/TitleBrowserV2/TitleBrowserItemMenuV2/TitleBrowserItemMenuV2';
import ListOverflowBadge from '@/components/_common/ListOverflowBadge/ListOverflowBadge';

type TitleBrowserItemV2Props = {
  data: TitleEntityV2;
  level: number;
};

const TitleBrowserItemV2 = ({ data, level }: TitleBrowserItemV2Props) => {
  const { id, name, entity_type, relationships } = data;
  const children = relationships?.children || [];
  const { error: errorToast } = useToast();
  const dispatch = useAppDispatch();
  const { checkEmbargoedVersions, disableAddVersionFn, disableSelectAll, displayTitleMetadata, expandedVersionRows, searchTermFilter } =
    useConfigValue(['orderPlacementConfig']);
  const parentTitle = useAppSelector(selectRootTitle);
  const packagingActiveTemplate = useAppSelector(selectPackagingActiveTemplate);
  const isChildrenLoading = useAppSelector((state) => selectChildrenLoadingStatusByTitleId(state, id)) === LoadingStatuses.pending;
  const isVersionsLoading = useAppSelector((state) => selectIsActiveVersionsLoadingByTitle(state, id));
  const isLoading = isChildrenLoading || isVersionsLoading;
  const hasError = useAppSelector((state) => selectChildrenLoadingStatusByTitleId(state, id)) === LoadingStatuses.rejected;
  const currentTitleChildren = useAppSelector((state) => selectChildrenByTitleId(state, id));
  const activeVersions = useAppSelector((state) => selectActiveVersionsByTitleId(state, id));
  const metadata = useAppSelector((state) => selectTitleMetadataById(state, id));
  const selectedHierarchy = useAppSelector(selectSelectedHierarchy);
  const searchValue = useAppSelector(selectSearchValue);
  const activeTitleLevel = useAppSelector(selectActiveTitleLevel);
  const selectedTitleId = useAppSelector(selectSelectedTitleId);
  const canCreateEmbargoedTitleOrderPending = useAppSelector(selectCanCreateEmbargoedTitleOrderPending);
  const canCreateEmbargoedTitleOrderProgress = useAppSelector(selectCanCreateEmbargoedTitleOrderProgress);
  const [displayedVersions, setDisplayedVersions] = useState<TitleEntityV2[]>([]);
  const [searchedVersion, setSearchedVersion] = useState<TitleEntityV2[] | undefined>(undefined);
  const [viewInactive, setToggleViewInactive] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const isLastChild = !children.length;
  const hasVersions = children.find((c) => c.entity_type === 'version');
  const versionsChildren = currentTitleChildren?.filter((c) => c.entity_type === 'version') || [];
  const shouldFilterInactive = versionsChildren.every((version) => version.meta?.is_active !== undefined);
  const currentBreakpoint = useCurrentBreakpoint();

  const handleAccordionExpand = (isExpanded: boolean) => {
    setExpanded(isExpanded);
    dispatch(setSelectedTitleId(id));
    dispatch(setActiveTitleLevel(level));
    dispatch(updateActiveTitleHierarchy({ id, level }));

    if (level >= 3) {
      dispatch(setActiveTitleId(id));
      if (!currentTitleChildren) {
        dispatch(fetchTitleV2(id))
          .unwrap()
          .then((titleData) => {
            if (titleData.relationships.children.every((child) => child.entity_type === 'version')) {
              dispatch(getTitleVersions(titleData.id));
            }
          })
          .catch((err) => errorToast(err.message || 'There was a problem loading the title.'));
        dispatch(fetchTitleChildrenV2(id));
      }
    } else if (!isLastChild && !hasVersions && !currentTitleChildren && isExpanded) {
      dispatch(fetchTitleChildrenV2(id));

      if (displayTitleMetadata) {
        dispatch(
          fetchTitleMetadata({
            body: {
              language: 'en-US',
              hierarchy: selectedHierarchy,
              entity_ids: children.map((c) => c.id)
            }
          })
        );
      }
      if (!activeVersions || activeVersions?.length === 0) {
        dispatch(getTitleVersions(id));
      }
    }
  };

  const disableAddVersion = useCallback(
    (version: TitleEntityV2): boolean => {
      const disable = disableAddVersionFn
        ? disableAddVersionFn(data, selectedHierarchy, {
            canCreateEmbargoedTitleOrderPending,
            canCreateEmbargoedTitleOrderProgress,
            version
          })
        : false;

      return !currentTitleChildren?.find((title) => title.name.toLowerCase() === version.name || '') || version.disabled || disable;
    },
    [currentTitleChildren, data, selectedHierarchy, disableAddVersionFn, canCreateEmbargoedTitleOrderPending, canCreateEmbargoedTitleOrderProgress]
  );

  const toggleActive = () => {
    setToggleViewInactive(!viewInactive);
  };

  useEffect(
    function handleErrorOnFetch() {
      if (!isLoading && hasError) {
        errorToast('Could not get title children. Please try again later.');
      }
    },
    [isLoading, hasError]
  );

  useEffect(
    function fetchVersionsIfLastChildAndHasVersions() {
      if (hasVersions && (!activeVersions || activeVersions?.length === 0) && !isLoading) {
        dispatch(getTitleVersions(id));
      }
    },
    [children, id, isLoading]
  );

  useEffect(
    function handleDisplayedVersions() {
      let activeVersions = [...versionsChildren];
      if (shouldFilterInactive && !viewInactive) {
        activeVersions = versionsChildren?.filter((x) => x.meta?.is_active);
      }
      if (typeof searchTermFilter === 'function') setSearchedVersion(searchTermFilter(activeVersions, searchValue));
      setDisplayedVersions(activeVersions);
    },
    [viewInactive, parentTitle?.id, currentTitleChildren]
  );

  if (hasError) return null;
  if (isLastChild || hasVersions) {
    return <TitleBrowserEntity entity={data} metadata={metadata} />;
  }

  const childType = children[0].entity_type;
  const versionChipList = Array.isArray(searchedVersion) && searchedVersion.length ? searchedVersion : displayedVersions;
  return (
    <TitleBrowserAccordion
      defaultExpanded={selectedTitleId === id && level === activeTitleLevel}
      expandedCallback={(isExpanded) => handleAccordionExpand(isExpanded)}
      title={
        <TitleRow>
          <Grid container alignItems="center">
            <Grid item component={Typography} sx={{ ml: 1 }}>
              {name}
            </Grid>

            <Grid item display="flex" flexGrow={1}>
              <Grid item display="flex" alignItems="center" sx={{ opacity: 0.5 }}>
                <span style={{ margin: '0 10px' }}>|</span>
                <Typography>
                  {children.length} {entity_type === 'marketing' ? 'Marketing Item' : childType}
                  {children.length !== 1 ? 's' : ''}
                </Typography>
              </Grid>
              {shouldFilterInactive && expanded && displayedVersions.length > 0 && (
                <StyledOnOffToggleWrapper breakpoint={currentBreakpoint}>
                  <OnOffToggle on={viewInactive} onToggle={toggleActive} label="View inactive versions" />
                </StyledOnOffToggleWrapper>
              )}
              {!expanded && versionChipList.length > 0 && (
                <StyledGrid item>
                  <ListOverflowBadge
                    list={versionChipList.map((version) => {
                      const isEmbargoed = checkEmbargoedVersions && version.is_embargoed && disableAddVersion(version);
                      return (
                        <TitleVersionChip
                          withTooltip
                          key={version.id}
                          version={version}
                          disabled={disableAddVersion(version)}
                          tooltip={isEmbargoed ? 'Embargoed cannot be selected' : undefined}
                          tooltipSource="global-channel-ids"
                        />
                      );
                    })}
                    delineated={false}
                  />
                </StyledGrid>
              )}
            </Grid>
            {
              // If `packagingActiveTemplate` is not null, this mean this component is rendered on the Packaging page,
              // and in that case we don't want to display "Add all" dropdown (`TitleBrowserItemMenu` component)
              !packagingActiveTemplate && children.length > 0 && !disableSelectAll && (
                <>
                  <div style={{ flexGrow: 1 }} />
                  <TitleBrowserItemMenuV2 parentId={id} />
                </>
              )
            }
          </Grid>
        </TitleRow>
      }
    >
      {isLoading ? (
        <Skeleton variant="rounded" width="100%" height={80} sx={{ marginTop: 0, marginBottom: '1rem' }} />
      ) : (
        <>
          {displayedVersions?.length > 0 && (
            <Grid container>
              {expandedVersionRows ? (
                expandedVersionRows(displayedVersions, searchedVersion, true, selectedHierarchy, {
                  canCreateEmbargoedTitleOrderPending,
                  canCreateEmbargoedTitleOrderProgress
                })
              ) : (
                <ExpandedVersionRow versions={displayedVersions} />
              )}
            </Grid>
          )}
          {currentTitleChildren?.map((child) => <TitleBrowserItemV2 key={child.id} level={level + 1} data={child} />)}
        </>
      )}
    </TitleBrowserAccordion>
  );
};

export default memo(TitleBrowserItemV2);

const TitleRow = styled.div(({ theme }) => ({
  justifyContent: 'flex-start',
  width: '100%',
  padding: theme.spacing(1.5, 2, 1.5, 0),
  color: theme.palette.text.primary,
  textTransform: 'none',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  flexDirection: 'row',
  marginRight: 10,

  '.MuiTypography-root, span': {
    fontSize: '1.2rem',
    whiteSpace: 'nowrap'
  }
}));

const StyledGrid = styled(Grid)(() => ({
  overflow: 'hidden',
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  flex: 'auto',

  '& > div': {
    flexDirection: 'row-reverse'
  }
}));

const StyledOnOffToggleWrapper = styled.div<{ breakpoint: string }>(({ theme, breakpoint }) => ({
  justifyContent: ['lg', 'xl'].includes(breakpoint) ? 'flex-end' : 'flex-start',
  alignItems: 'center',
  display: 'flex',
  '&': ['lg', 'xl'].includes(breakpoint)
    ? {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(2)
      }
    : {}
}));
